import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Gavel as BrandIcon,
  LockOutlined as LockIcon,
  PersonOutline as PersonIcon,
  CheckCircleOutline as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getInvitation,
  acceptInvitation,
} from '../../services/staff';
import { Invitation, ROLE_LABEL, ROLE_DESCRIPTION } from '../../types/staff';

const PENDING_INVITE_TOKEN_KEY = 'huduma_pending_invite_token';

const NAVY = '#1E3A5F';
const ACCENT = '#3B82F6';

const ROLE_CHIP_COLOR = {
  admin: { bg: '#FEF3C7', fg: '#92400E' },
  manager: { bg: '#EFF6FF', fg: '#1D4ED8' },
  staff: { bg: '#F3F4F6', fg: '#374151' },
} as const;

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [invite, setInvite] = useState<Invitation | null>(null);
  const [loading, setLoading] = useState(true);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setLookupError('Missing invitation token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const inv = await getInvitation(token);
        if (cancelled) return;
        if (!inv) {
          setLookupError('This invitation link is not valid.');
        } else if (inv.status === 'accepted') {
          setLookupError('This invitation has already been accepted.');
        } else if (inv.status === 'revoked') {
          setLookupError('This invitation has been revoked.');
        } else if (inv.expiresAt.toMillis() < Date.now()) {
          setLookupError('This invitation has expired.');
        } else {
          setInvite(inv);
        }
      } catch (err) {
        console.error(err);
        setLookupError('We couldn’t load this invitation. Please try the link again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!invite) return;

    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // Stash the invite token so AuthContext can claim it as a recovery
      // path if `acceptInvitation`'s own setDoc loses the race with the
      // auth state change.
      sessionStorage.setItem(PENDING_INVITE_TOKEN_KEY, invite.id);
      await acceptInvitation(invite.id, password, displayName.trim() || undefined);
      sessionStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
      toast.success('Welcome aboard');
      navigate('/');
    } catch (err: any) {
      // Do NOT remove the sessionStorage token in the error case — if the
      // failure was the AuthContext race, the token will let AuthContext
      // finish the acceptance after createUserWithEmailAndPassword succeeded.
      console.error(err);
      const code = err?.code as string | undefined;
      if (code === 'auth/email-already-in-use') {
        setSubmitError(
          'An account already exists for this email. Sign in instead, then ask an admin to assign your role.',
        );
      } else if (code === 'auth/weak-password') {
        setSubmitError('Password is too weak. Choose a stronger one.');
      } else if (err instanceof Error) {
        setSubmitError(err.message);
      } else {
        setSubmitError('Could not complete signup.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* --- Loading state --- */

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  /* --- Error / invalid invitation --- */

  if (!invite) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default',
          px: 4,
        }}
      >
        <Paper sx={{ p: 6, maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#FEE2E2',
              color: '#991B1B',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <PersonIcon />
          </Box>
          <Typography sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
            Invitation unavailable
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 4 }}>
            {lookupError ?? 'This invitation link can no longer be used.'}
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </Paper>
      </Box>
    );
  }

  /* --- Happy path: signup form --- */

  const roleColors = ROLE_CHIP_COLOR[invite.role];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        px: { xs: 4, sm: 6 },
        py: 8,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 0%, ${ACCENT}10 0%, transparent 40%), radial-gradient(circle at 100% 100%, ${NAVY}10 0%, transparent 40%)`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', maxWidth: 460 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 5,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: NAVY,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0 4px 12px rgba(30,58,95,0.25)',
            }}
          >
            <BrandIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
          </Box>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>Huduma</Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
            Client Records
          </Typography>
        </Box>

        <Paper
          sx={{
            p: { xs: 6, sm: 7 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 3,
              mb: 4,
              borderRadius: 2,
              bgcolor: '#F9FAFB',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CheckIcon sx={{ fontSize: 20, color: 'success.main', mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                You've been invited
              </Typography>
              <Typography
                sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 1.5 }}
              >
                {invite.email} — joining as{' '}
                <Chip
                  label={ROLE_LABEL[invite.role]}
                  size="small"
                  sx={{
                    bgcolor: roleColors.bg,
                    color: roleColors.fg,
                    fontWeight: 600,
                    ml: 0.5,
                    height: 20,
                  }}
                />
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                {ROLE_DESCRIPTION[invite.role]}
              </Typography>
            </Box>
          </Box>

          <Typography
            component="h1"
            sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
          >
            Set up your account
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 4 }}>
            Choose a password to finish creating your account.
          </Typography>

          {submitError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {submitError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Box sx={{ mb: 3 }}>
              <Typography
                component="label"
                htmlFor="accept-name"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Display name (optional)
              </Typography>
              <TextField
                id="accept-name"
                fullWidth
                placeholder="Jane Doe"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                component="label"
                htmlFor="accept-password"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Password
              </Typography>
              <TextField
                id="accept-password"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((s) => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <VisibilityOffIcon sx={{ fontSize: 18 }} />
                        ) : (
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ mb: 5 }}>
              <Typography
                component="label"
                htmlFor="accept-confirm"
                sx={{
                  display: 'block',
                  fontSize: '0.8125rem',
                  fontWeight: 500,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Confirm password
              </Typography>
              <TextField
                id="accept-confirm"
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={submitting}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={submitting || !password || !confirm}
              startIcon={
                submitting ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
              sx={{ minHeight: 44 }}
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AcceptInvite;
